import React, { useEffect, useRef, useState } from "react";
import {
  CategoryPath,
  pathParents,
  pathTitle,
  readRecentPaths,
  rememberPath,
  searchCategoryPaths,
} from "./categoryPathSearch";

type Props = {
  /** Current form values, used for the closed state. */
  value: { artform?: any; type?: any; category?: any; subCategory?: any; weaveType?: any };
  onPick: (path: CategoryPath) => void;
  /** Opens the level-by-level selects (kept for adding a new sub-category or weave). */
  onShowLevels: () => void;
  levelsOpen: boolean;
};

const labelOf = (v: any): string => (v && typeof v === "object" ? v.label || v.name || "" : "");

/** One search box for all five classification levels: type "butid", pick "Saree › Flower butidaar motifs › Kadhua". */
const CategoryPathPicker = ({ value, onPick, onShowLevels, levelsOpen }: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Results are kept with the query they answer, so Enter or a tap never picks a row from an older search.
  const [results, setResults] = useState<{ query: string; rows: CategoryPath[] }>({ query: "", rows: [] });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults({ query: "", rows: [] });
      return;
    }
    const mine = ++seq.current;
    setBusy(true);
    const timer = window.setTimeout(() => {
      searchCategoryPaths(q).then((rows) => {
        if (mine !== seq.current) return;
        setResults({ query: q, rows });
        setBusy(false);
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const pick = (path: CategoryPath) => {
    rememberPath(path);
    onPick(path);
    setOpen(false);
    setQuery("");
  };

  const leaf = [labelOf(value.category), labelOf(value.subCategory), labelOf(value.weaveType)].filter(Boolean).join(" › ");
  const parents = [labelOf(value.artform), labelOf(value.type)].filter(Boolean).join(" › ");
  const recent = readRecentPaths();
  const q = query.trim();
  const list = q.length >= 2 ? (results.query === q ? results.rows : []) : recent;

  return (
    <div className="ps-cat">
      <div className="ps-label">
        <span>Category</span>
        <i>one search fills all 5 levels</i>
      </div>
      {!open ? (
        <button type="button" className="ps-cat__closed" onClick={() => setOpen(true)}>
          <span className="ps-cat__text">
            <strong>{leaf || "Search: saree, butidar, jangla…"}</strong>
            {parents ? <small>{parents}</small> : null}
          </span>
          <span aria-hidden="true">▾</span>
        </button>
      ) : (
        <div className="ps-cat__open">
          <input
            ref={inputRef}
            type="search"
            className="form-control ps-cat__input"
            placeholder="Type a category, sub-category or weave"
            value={query}
            enterKeyHint="search"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "Enter" && list[0]) {
                e.preventDefault();
                pick(list[0]);
              }
            }}
          />
          <div className="ps-cat__sec">
            {query.trim().length >= 2 ? (busy ? "Searching…" : list.length ? "Pick once, all levels fill" : "No match") : recent.length ? "Recent" : "Type at least 2 letters"}
          </div>
          <ul className="ps-cat__list" role="listbox">
            {list.map((path) => (
              <li key={path.key}>
                <button type="button" role="option" aria-selected={false} onClick={() => pick(path)}>
                  <strong>{pathTitle(path)}</strong>
                  <small>{pathParents(path)}</small>
                </button>
              </li>
            ))}
          </ul>
          <div className="ps-cat__foot">
            <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => setOpen(false)}>
              <span>Close</span>
            </button>
            <button
              type="button"
              className="ps-btn ps-btn--ghost ps-btn--sm"
              onClick={() => {
                setOpen(false);
                onShowLevels();
              }}
            >
              <span>Not listed? Choose level by level</span>
            </button>
          </div>
        </div>
      )}
      {!open && !levelsOpen ? (
        <button type="button" className="ps-cat__levels" onClick={onShowLevels}>
          Choose level by level or add a new one
        </button>
      ) : null}
    </div>
  );
};

export default CategoryPathPicker;
